/**
 * Доля хвоста публичного рендерера: вид `toggle` (переключатель).
 *
 * WEB-2920, заход 4: перенесено из `PublicBlockRendererExtended.tsx` слово в
 * слово. Метка `case 'toggle'` остаётся в фасаде; ветка вида — функция
 * отрисовки `renderToggle(ctx)`. Замыкания фасада приходят объектом-контекстом
 * (`ExtendedRenderContext`); ответ посетителя ветка читает из `values` и пишет
 * через `setValue` — это `useFormValues()` фасада: хук стоит до `switch`, в
 * функции отрисовки его звать нельзя (рисуется одна ветка за рендер). Эту
 * долю импортирует только фасад: она едет в ленивом куске `extended`, а не в
 * первом экране (самопроверка обхода).
 */
import React, { useEffect } from 'react';
// WEB-1651-3: цвет самой отметки нативных чекбоксов/радио (accent-color).
import { resolveOptionAccent } from '../optionAccentColor';
import type { ExtendedRenderContext } from '../PublicBlockRendererExtended';
import { useFormValues } from '../FormValuesContext';

/**
 * WEB-2451 п.12, решение владельца 06.10 (В100, вариант А): переключатель,
 * включённый по умолчанию и не тронутый посетителем, даёт ответ «включено» —
 * он виден «Ответам», правилам «Логики» и снимает обязательность. Засев — при
 * появлении блока, по образцу маркера `'done'` лоадера, дверью умолчаний
 * `setDefaultValue` (уже записанный ответ — выбор посетителя или прошлый
 * засев — не перебивается). После перезапуска квиза (`resetValues`) ответа
 * снова нет — эффект засевает заново. Скрытые логикой блоки не рисуются и не
 * засеваются. Выключенный по умолчанию переключатель ответа не пишет, как
 * раньше. Отдельный компонент: ветка `renderToggle` — функция, хук в ней не
 * позвать.
 */
function ToggleBlock({
  ctx,
}: {
  ctx: ExtendedRenderContext;
}): React.ReactElement {
  const { block, richText, setValue, values } = ctx;
  const { setDefaultValue } = useFormValues();
  const seeded = values[block.id];
  useEffect(() => {
    if (block.defaultToggled === true && seeded === undefined) {
      setDefaultValue(block.id, 'true');
    }
  }, [block.defaultToggled, block.id, seeded, setDefaultValue]);
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 16,
        cursor: 'pointer',
        color: 'var(--color-text-primary, inherit)',
      }}
    >
      <input
        type="checkbox"
        defaultChecked={block.defaultToggled}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setValue(block.id, e.target.checked ? 'true' : 'false')
        }
        // WEB-1651-3: переключатель рисуется тем же нативным чекбоксом.
        style={{
          width: 18,
          height: 18,
          accentColor: resolveOptionAccent(block.optionAccentColor),
        }}
      />
      <span style={{ fontSize: block.fontSize ?? 16 }}>
        {richText(block.content)}
      </span>
    </label>
  );
}

export function renderToggle(
  ctx: ExtendedRenderContext,
): React.ReactElement {
  return <ToggleBlock ctx={ctx} />;
}
